import { yupResolver } from '@hookform/resolvers/yup';
import { Box, IconButton, DialogContent, Grid, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BsPencilSquare } from 'react-icons/bs';
import {
  ICommunicationServiceAccount,
  IRecruiterDTO,
  postAdvancedQuery,
  sharePhoneNumber,
} from 'wisestep-api';
import {
  WsTypography,
  WsAutoComplete,
  WsButton,
  WsDialog,
  WsTypoVariants,
  ZoomLoader,
  WsPageHeader,
  WsDrawer,
  WsDataGrid,
  SearchBarNew,
} from 'wisestep-base';
import { array, object, string } from 'yup';

import { useWorkspaceContext } from '../../../context';
import {
  useSmsConfigAccessControl,
  SmsConfigAction,
} from '../../../hooks/useSmsConfigAccessControl';
import Zoom from '../../../images/icons/Zoom.png';
import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import {
  mapZoomAccounts,
  recruiterAdvancedQueryPayload,
  sharePhoneNumberPayload,
} from '../helper/payload';

import ZoomAccountSetting from './ZoomAccountSetting';

const schema = object().shape({
  assignedRecruiters: array()
    .of(string().email('Please enter a valid email'))
    .min(0)
    .max(1, 'You can only assign 1 recruiters'),
});

export interface ICommunicationAccountsProps {
  communicationAccounts: ICommunicationServiceAccount[];
  credit?: number;
  deleteable?: boolean;
  refresh: () => Promise<void>;
  refreshLocalStorage: () => Promise<void>;
  getAccounts: () => Promise<void>;
  refreshZoomAccounts: () => Promise<void>;
  setZoomAccounts: any;
}

export interface IZoomAccount {
  id: string | number;
  phoneNumber?: string;
  email?: string;
  users?: number;
  sharedWith?: string[];
  action?: any;
}

const ZoomAccounts = ({
  communicationAccounts,
  credit,
  refreshLocalStorage,
  getAccounts,
  refreshZoomAccounts,
}: ICommunicationAccountsProps) => {
  const [accounts, setAccounts] = useState<ICommunicationServiceAccount[]>(communicationAccounts);
  const admin = useWorkspaceContext();
  const [loader, setLoader] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);

  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>('');

  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [serviceId, setServiceId] = useState<string>('');

  const [initialAssignedRecruiters, setInitialAssignedRecruiters] = useState<string[]>([]);

  const [recruiterSuggestions, setRecruiterSuggestions] = useState<string[]>([]);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [zoomData, setZoomData] = useState<IZoomAccount[]>(mapZoomAccounts(communicationAccounts));
  const [rowNo, setRowNo] = useState<number>(-1);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const { access } = useSmsConfigAccessControl([
    SmsConfigAction.MANAGE_SMS_CONFIGURATION,
    SmsConfigAction.DELETE_SMS_CONFIGURATION,
    SmsConfigAction.MANAGE_SMS_USERS,
    SmsConfigAction.MANAGE_SMS_LOGS,
  ]);
  const [manageSmsConfig, manageSmsUsers] = access;

  useEffect(() => {
    setAccounts(communicationAccounts);
  }, [communicationAccounts]);

  useEffect(() => {
    if (
      null === communicationAccounts ||
      undefined === communicationAccounts ||
      communicationAccounts.length === 0
    ) {
      getAccounts();
    }
  });

  const defaultValues = {
    assignedRecruiters: initialAssignedRecruiters,
    query: '',
  };

  const confirm = (value: boolean) => setOpenModal(value);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });
  const { getValues } = methods;

  const handleDrawer = (value: boolean, index: number) => {
    setSelectedPhoneNumber(communicationAccounts?.[0]?.phoneNumbers?.[index]?.phoneNumber);
    setSelectedAccountId(communicationAccounts?.[0]?.id);
    setInitialAssignedRecruiters(
      communicationAccounts?.[0]?.phoneNumbers?.[index]?.sharedWith ?? []
    );
    setSelectedOwner(communicationAccounts?.[0]?.phoneNumbers?.[index].owners?.[0] ?? '');
    setServiceId(communicationAccounts?.[0]?.serviceAccountId ?? '');
    setOpenDrawer(value);
    setRowNo(index);
  };

  useEffect(() => {
    setZoomData(mapZoomAccounts(accounts));
    if (rowNo >= 0) {
      setSelectedPhoneNumber(accounts?.[0]?.phoneNumbers?.[rowNo]?.phoneNumber);
      setSelectedAccountId(accounts?.[0]?.id);
      setInitialAssignedRecruiters(accounts?.[0]?.phoneNumbers?.[rowNo]?.sharedWith ?? []);
      setSelectedOwner(accounts?.[0]?.phoneNumbers?.[rowNo]?.owners?.[0] ?? '');
      setServiceId(accounts?.[0]?.serviceAccountId ?? '');
    }
  }, [accounts]);

  const assignRecruiter = async () => {
    const selected = methods.getValues('assignedRecruiters');
    const response = await sharePhoneNumber({
      ...sharePhoneNumberPayload,
      accountId: selectedAccountId,
      emails: selected,
      phoneNumber: selectedPhoneNumber,
    });
    setOpenModal(false);
    response.data.status === 'SHARED'
      ? setNotification(success('Phone Number shared with recruiter successfully!'))
      : setNotification(failed(response.data.status));
    refreshLocalStorage();
  };

  const handleInput = async (input: string) => {
    const response = await postAdvancedQuery({
      ...recruiterAdvancedQueryPayload,
      recruiterEmailPhrase: input,
      recruiterFirstNamePhrase: input,
      recruiterLastNamePhrase: input,
    });
    setRecruiterSuggestions([]);
    response.data.map((recruiter: IRecruiterDTO) =>
      setRecruiterSuggestions((prev) => [...prev, recruiter.email])
    );
  };
  //in future we need this function so i kept it commented
  // const handleDeleteAccount = useCallback(async () => {
  //   const accounts = JSON.parse(localStorage.getItem('communicationAccounts') ?? '[]');
  //   const accountId = accounts.filter((o) => o.serviceName === 'ZOOM')?.[0]?.id;
  //   setLoader(true);
  //   await deleteCommunicationAccount(accountId);
  //   await refreshLocalStorage();
  //   logOutZoom();
  //   setZoomAccounts([]);
  //   getAccounts();
  //   setLoader(false);
  // }, [getAccounts, refreshLocalStorage, setZoomAccounts]);

  const columns: GridColDef[] = [
    {
      headerName: 'Phone Numbers',
      field: 'phoneNumber',
      flex: 1,
      renderCell: (params) => (
        <WsTypography variant={WsTypoVariants.BODY1} bold>
          {params.row.phoneNumber}
        </WsTypography>
      ),
    },

    {
      headerName: 'Email',
      field: 'email',
      flex: 1,
    },

    {
      headerName: 'Users',
      field: 'sharedWith',
      renderCell: (params) => {
        return (
          <WsTypography variant={WsTypoVariants.BODY1} bold>
            {params.row.users}
          </WsTypography>
        );
      },
    },

    {
      headerName: 'Action',
      field: 'owners',
      renderCell: (params) => {
        return (
          <IconButton
            onClick={() => handleDrawer(true, params.row.id)}
            disableRipple
            disabled={!(admin.owns || manageSmsConfig || manageSmsUsers)}
          >
            <BsPencilSquare />
          </IconButton>
        );
      },
    },
  ];

  const handleRefresh = async () => {
    setLoader(true);
    await refreshZoomAccounts();
    setLoader(false);
  };

  const handleSearch = () => {
    const search = getValues('query');
    const results = zoomData.filter(
      (item) =>
        item?.phoneNumber?.includes(search) ||
        item?.email?.toLowerCase()?.includes(search.toLowerCase()) ||
        item?.sharedWith?.some((email) => email.toLowerCase()?.includes(search.toLowerCase()))
    );
    setZoomData(results);
  };
  const handleClear = () => {
    setZoomData(mapZoomAccounts(communicationAccounts));
  };

  return loader ? (
    <ZoomLoader />
  ) : (
    <FormProvider {...methods}>
      <WsPageHeader
        title="Zoom"
        {...(credit ? { text: `Credits earned: ${credit}` } : {})}
        buttonTitle="Refresh Account"
        onButtonClick={handleRefresh}
        transparent={true}
        profileImg={{ profileImage: Zoom, required: true }}
      />
      <Grid item xs={5} sx={{ ml: 2 }}>
        <SearchBarNew
          size="small"
          name="query"
          placeholder="Search"
          type="submit"
          onClear={handleClear}
          onSearch={handleSearch}
        />
      </Grid>
      <Box>
        <WsDataGrid
          autoHeight
          rows={zoomData}
          columns={columns}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Box>
      {openModal && (
        <WsDialog size="medium" openDialog={openModal}>
          <DialogContent sx={{ p: 2 }}>
            <WsTypography variant={WsTypoVariants.TITLE}>
              Select Recruiter to Assign Phone Number : {selectedPhoneNumber}
            </WsTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} mt={2}>
                <Stack direction="column">
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>Email Address</WsTypography>
                </Stack>
                <WsAutoComplete
                  name="assignedRecruiters"
                  fullWidth
                  size="small"
                  multiple={false}
                  initialValue={initialAssignedRecruiters}
                  optionsInStringArray={recruiterSuggestions}
                  onValueEnter={handleInput}
                  onTagsChange={(tags) => methods.setValue('assignedRecruiters', tags)}
                />
              </Grid>
              <Grid item xs={12} spacing={2}>
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <WsButton variant="outlined" size="large" onClick={() => confirm(false)}>
                    Cancel
                  </WsButton>
                  <WsButton type="submit" size="large" onClick={assignRecruiter}>
                    Assign
                  </WsButton>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
        </WsDialog>
      )}
      {openDrawer && (
        <WsDrawer
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          title="Zoom Account Settings"
        >
          <ZoomAccountSetting
            userId={selectedOwner}
            phoneNumber={selectedPhoneNumber}
            accountId={selectedAccountId}
            initialAssignedRecruiters={initialAssignedRecruiters}
            refreshLocalStorage={() => refreshLocalStorage()}
            serviceId={serviceId}
          />
        </WsDrawer>
      )}
    </FormProvider>
  );
};

export default ZoomAccounts;
